import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import {
  Activity, User, Clock, FileText, Briefcase, CheckCircle2,
  Edit, Trash2, Plus, LogIn, LogOut, Search, Filter, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ICONS = {
  login: LogIn, logout: LogOut, create: Plus, update: Edit, delete: Trash2,
  task: CheckCircle2, client: Briefcase, user: User, work_log: FileText
};

export default function CRMActivityLog() {
  const { token } = useCRMAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => { fetchLogs(); }, [typeFilter, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const typeParam = typeFilter !== 'all' ? `&type=${typeFilter}` : '';
      const res = await fetch(`${API}/api/crm/activity?page=${page}&limit=50${typeParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(page === 1 ? data.logs : [...logs, ...data.logs]);
        setHasMore(data.logs.length === 50);
      }
    } catch (err) {}
    finally { setLoading(false); }
  };

  const filtered = logs.filter(log =>
    log.description?.toLowerCase().includes(search.toLowerCase()) ||
    log.user_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getIcon = (log) => {
    const Icon = ICONS[log.action] || ICONS[log.entity_type] || Activity;
    return Icon;
  };

  const getColor = (action) => {
    const colors = { create: 'text-emerald-600 bg-emerald-100', update: 'text-blue-600 bg-blue-100', delete: 'text-red-600 bg-red-100', login: 'text-purple-600 bg-purple-100', logout: 'text-slate-600 bg-slate-100' };
    return colors[action] || 'text-slate-600 bg-slate-100';
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = (now - d) / 1000;
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activity Log</h1>
          <p className="text-slate-500">Track all actions and changes</p>
        </div>
      </div>

      <Card className="p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search activity..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[150px]">
              <Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="task">Tasks</SelectItem>
              <SelectItem value="client">Clients</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="work_log">Work Logs</SelectItem>
              <SelectItem value="auth">Auth</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading && page === 1 ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No activity found</h3>
          <p className="text-slate-500">Activity will appear here as actions are performed</p>
        </Card>
      ) : (
        <Card className="divide-y">
          {filtered.map(log => {
            const Icon = getIcon(log);
            return (
              <div key={log.id} className="p-4 hover:bg-slate-50 flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getColor(log.action)}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">{log.description}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{log.user_name}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatTime(log.created_at)}</span>
                    {log.entity_type && <Badge variant="outline" className="text-xs">{log.entity_type}</Badge>}
                  </div>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div className="p-4 text-center">
              <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Load More'}
              </Button>
            </div>
          )}
        </Card>
      )}
    </CRMLayout>
  );
}
